// Who may do what, defined once and read by both ends. §25f.
//
// **The package earns its place on one property**: the server enforces these
// rules and the admin screen has to *draw* them — an Approve button somebody
// cannot use is a button that lies — and CASL lets both come from this one
// definition. That is the argument this codebase already makes for `laneFed`
// and `hopsToAlly`: two readings of one rule drift, and the
// drift here is a control that appears for people the server then refuses.
//
// It is not here for expressive power. Three roles and four actions would be a
// forty-line `can()` — what would be hand-rolled is the *sharing*, and that is
// the part that goes wrong quietly.
//
// **In `src/` because both sides import it.** The server already reaches into
// `src/engine/net/protocol` and `src/engine/flags/flags` for exactly this reason. It reaches
// nothing in `src/engine/rules/` and nothing in `src/engine/rules/` reaches it: a permission
// is not a rule of the game, and a reducer that consulted one would derive two
// different boards from one log.

import { AbilityBuilder, createMongoAbility, type MongoAbility } from "@casl/ability";

/**
 * The roles, least to most.
 *
 * Deliberately few. A role per person is a permission system nobody can hold in
 * their head, and the question this has to answer is "can they let testers in"
 * rather than "which of forty verbs".
 */
export const ROLES = ["tester", "approver", "owner"] as const;
export type Role = (typeof ROLES)[number];

export const isRole = (value: unknown): value is Role =>
  typeof value === "string" && (ROLES as readonly string[]).includes(value);

/** What the roles are called and what they are for, shown in the screen itself. */
export const ROLE_DETAIL: Record<Role, string> = {
  tester: "Plays the game. No admin access.",
  approver: "Lets testers in, reads the tuning dashboard, and has the developer tools.",
  owner: "Can also change what other people are."
};

/** Everything anybody may do. A union, so a typo is a type error. */
export type Action = "read" | "approve" | "setRole";
/**
 * What those actions are done to.
 *
 * `Tuner` covers the tuning dashboard and the rollout board — the screens that
 * were behind a shared password. **A password answers "does this browser know a
 * secret" and cannot answer "who is asking"**, so it could not be given to one
 * person or taken from one person; rotating it logged everybody out. A role can
 * be granted and revoked per account, which is the whole reason to move them.
 *
 * `Dev` is the developer panel — grow a camp, defeat an army, grant a card,
 * swap the piece pack. **Its own subject rather than a second reading of
 * `Tuner`**, because the two are different in kind: the tuning screens are
 * telemetry somebody *reads*, and this one *conjures armies, ground and
 * material*. They happen to land on the same roles today, and a subject each
 * is what lets that stop being true without unpicking anything.
 */
export type Subject = "User" | "Tuner" | "Dev";
export type Ability = MongoAbility<[Action, Subject]>;

/**
 * The rules for one role.
 *
 * **`owner` is not "everything"** — it is spelled out, because `manage all` is
 * how a permission system stops being readable and starts being a thing you
 * test by trying it.
 */
export function abilityFor(role: Role): Ability {
  const { can, build } = new AbilityBuilder<Ability>(createMongoAbility);
  if (role === "approver" || role === "owner") {
    can("read", "User");
    can("approve", "User");
    // Staff see the internals. It is telemetry about how the opponent is
    // trained rather than anything a player's game depends on, so it rides
    // with the role that already decides who gets in rather than earning a
    // fourth role nobody could keep straight.
    can("read", "Tuner");
    // And staff get the developer panel on a deployed build. The thing it is
    // safe to hand out this widely is **not** that the tools are harmless —
    // they fabricate armies — it is that every command they send is in
    // `SOLO_COMMANDS`, so `server/rooms.ts` refuses the lot on a networked
    // room whoever is asking. What the role decides is who sees the panel in
    // their *own* single-player game, which is nobody else's board.
    can("read", "Dev");
  }
  if (role === "owner") {
    can("setRole", "User");
  }
  return build();
}

/** May somebody in this role reach the admin section at all? */
export const isStaff = (role: Role): boolean => abilityFor(role).can("read", "User");
